import type { KowryCatalogItem } from "./genesis002Catalog";

export interface CatalogNavigation {
  setLoading: (loading: boolean) => void;
  update: (item: KowryCatalogItem, index: number) => void;
}

export function connectCatalogNavigation(
  items: readonly KowryCatalogItem[],
  onSelect: (index: number) => void,
): CatalogNavigation {
  const name = requireElement<HTMLElement>("specimen-name");
  const position = requireElement<HTMLElement>("specimen-position");
  const previous = requireElement<HTMLButtonElement>("previous-kowry");
  const next = requireElement<HTMLButtonElement>("next-kowry");
  const reset = requireElement<HTMLButtonElement>("reset-kowry");
  const jumpForm = requireElement<HTMLFormElement>("kowry-jump");
  const jumpInput = requireElement<HTMLInputElement>("kowry-number");
  const jumpButton = jumpForm.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (!jumpButton) throw new Error("Missing Kowry jump submit button");
  let currentIndex = 0;

  previous.addEventListener("click", () => onSelect(wrap(currentIndex - 1, items.length)));
  next.addEventListener("click", () => onSelect(wrap(currentIndex + 1, items.length)));
  reset.addEventListener("click", () => onSelect(0));
  jumpInput.addEventListener("input", () => jumpInput.setCustomValidity(""));
  jumpInput.addEventListener("focus", () => jumpInput.select());
  jumpForm.addEventListener("submit", (event) => {
    event.preventDefault();
    const tokenNumber = Number(jumpInput.value);
    const index = items.findIndex((item) => item.tokenNumber === tokenNumber);
    if (!Number.isInteger(tokenNumber) || index < 0) {
      jumpInput.setCustomValidity(`Enter a whole number from 1 to ${items.length}.`);
      jumpInput.reportValidity();
      return;
    }
    jumpInput.setCustomValidity("");
    onSelect(index);
  });
  window.addEventListener("keydown", (event) => {
    if (event.defaultPrevented || isInteractiveTarget(event.target)) return;
    if (event.key === "ArrowLeft") onSelect(wrap(currentIndex - 1, items.length));
    if (event.key === "ArrowRight") onSelect(wrap(currentIndex + 1, items.length));
    if (event.key === "Home") onSelect(0);
  });

  return {
    setLoading(loading): void {
      previous.disabled = loading;
      next.disabled = loading;
      reset.disabled = loading;
      jumpInput.disabled = loading;
      jumpButton.disabled = loading;
    },
    update(item, index): void {
      currentIndex = index;
      name.textContent = item.name;
      position.textContent = `${index + 1} of ${items.length}`;
      reset.hidden = index === 0;
      jumpInput.value = String(item.tokenNumber);
    },
  };
}

export function catalogIndexFromUrl(items: readonly KowryCatalogItem[]): number {
  const url = new URL(window.location.href);
  const routeMatch = url.pathname.match(/\/collections\/kowry-net-genesis-002\/items\/(\d{4})\/?$/);
  const requested = routeMatch?.[1] ?? url.searchParams.get("kowry");
  if (!requested || !/^\d{1,4}$/.test(requested)) return 0;
  const tokenNumber = Number(requested);
  const index = items.findIndex((item) => item.tokenNumber === tokenNumber);
  return index >= 0 ? index : 0;
}

export function updateCatalogUrl(item: KowryCatalogItem, replace = false): void {
  const url = new URL(window.location.href);
  url.pathname = `/collections/kowry-net-genesis-002/items/${String(item.tokenNumber).padStart(4, "0")}/`;
  url.searchParams.delete("kowry");
  const method = replace ? "replaceState" : "pushState";
  window.history[method]({ kowry: item.tokenNumber }, "", url);
}

function wrap(index: number, length: number): number {
  return (index + length) % length;
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && Boolean(target.closest("button, a, input, select, textarea, summary"));
}

function requireElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing required element: #${id}`);
  return element as T;
}
